import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Typography } from "@material-tailwind/react";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import recipes from "../API/recipes";

export default function DetailSection() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const tagMap = useMemo(
    () => ({
      cheap: "Cheap",
      dairyFree: "Dairy Free",
      glutenFree: "Gluten Free",
      ketogenic: "Ketogenic",
      lowFodmap: "Low FODMAP",
      sustainable: "Sustainable",
      vegan: "Vegan",
      vegetarian: "Vegetarian",
      veryHealthy: "Very Healthy",
      veryPopular: "Very Popular",
      whole30: "Whole 30",
    }),
    []
  );

  const allTags = useMemo(
    () => [
      "cheap",
      "dairyFree",
      "glutenFree",
      "ketogenic",
      "lowFodmap",
      "sustainable",
      "vegan",
      "vegetarian",
      "veryHealthy",
      "veryPopular",
      "whole30",
    ],
    []
  );

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setLoading(true);
    setErr("");

    recipes
      .getRecepieDetail(id)
      .then((res) => {
        localStorage.setItem("interested", String(id));
        setData(res.data);
      })
      .catch(() => {
        setErr("We couldn’t load this recipe. Please try again.");
      })
      .finally(() => setLoading(false));
  }, [id]);

  const tags = useMemo(() => {
    if (!data) return [];
    return allTags.filter((k) => Boolean(data?.[k]));
  }, [data, allTags]);

  if (loading) {
    return (
      <section className="mx-auto max-w-3xl px-4 py-10">
        <div className="space-y-4">
          <div className="h-7 w-2/3 animate-pulse rounded bg-gray-200" />
          <div className="h-64 w-full animate-pulse rounded-xl bg-gray-200" />
          <div className="flex gap-2">
            <div className="h-7 w-20 animate-pulse rounded bg-gray-200" />
            <div className="h-7 w-24 animate-pulse rounded bg-gray-200" />
            <div className="h-7 w-28 animate-pulse rounded bg-gray-200" />
          </div>
          <div className="grid gap-2">
            <div className="h-6 w-40 animate-pulse rounded bg-gray-200" />
            <div className="h-6 w-3/4 animate-pulse rounded bg-gray-200" />
            <div className="h-6 w-2/3 animate-pulse rounded bg-gray-200" />
          </div>
        </div>
      </section>
    );
  }

  if (err) {
    return (
      <section className="mx-auto max-w-3xl px-4 py-10">
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-800">
          <p className="font-semibold">Something went wrong</p>
          <p className="text-sm opacity-90">{err}</p>
        </div>
        <Link
          to="/"
          className="mt-4 inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm"
        >
          <ArrowLeftIcon className="h-4 w-4" /> Go back home
        </Link>
      </section>
    );
  }

  if (!data) return null;

  return (
    <section className="mx-auto max-w-3xl px-4 py-10">
      <div className="mb-3 flex items-center gap-2 text-sm text-gray-500">
        <svg
          className="h-4 w-4"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M4 3h16M4 8h16M4 13h16M4 18h16" />
        </svg>
        Recipe Detail
      </div>

      <div className="overflow-hidden rounded-2xl border bg-white shadow">
        <div className="p-6">
          <Typography variant="h4" className="!mb-1 !leading-tight">
            {data.title}
          </Typography>
          <p className="text-sm text-gray-600">
            {data.readyInMinutes && (
              <span className="mr-2">Ready in {data.readyInMinutes} min</span>
            )}
            {data.servings && <span>• Serves {data.servings}</span>}
          </p>
        </div>

        <div className="px-6 pb-6">
          <div className="overflow-hidden rounded-xl border">
            <img
              src={data.image}
              alt={data.title}
              className="h-auto w-full object-cover"
              loading="eager"
            />
          </div>

          {/* Tags */}
          <div className="mt-4 flex flex-wrap gap-2">
            {tags.map((k) => (
              <span
                key={k}
                className="rounded-full border px-2.5 py-1 text-xs font-semibold text-gray-800"
              >
                {tagMap[k]}
              </span>
            ))}
          </div>

          {/* Ingredients */}
          <div className="mt-6">
            <h3 className="mb-2 text-lg font-semibold">Ingredients</h3>
            <ul className="list-disc space-y-1 pl-5 text-[15px] leading-7 text-gray-700">
              {data?.extendedIngredients?.map(({ original }, idx) => (
                <li key={idx}>{original}</li>
              ))}
            </ul>
          </div>

          {/* Instructions */}
          <div className="mt-6">
            <h3 className="mb-2 text-lg font-semibold">Instructions</h3>
            {data?.analyzedInstructions?.[0]?.steps?.length ? (
              <ol className="list-decimal space-y-3 pl-5 text-[15px] leading-7 text-gray-700">
                {data.analyzedInstructions[0].steps.map(
                  ({ step, number }) => (
                    <li key={number}>
                      <span className="font-medium">Step {number}.</span>{" "}
                      {step}
                    </li>
                  )
                )}
              </ol>
            ) : (
              <p className="text-gray-600">No instructions provided.</p>
            )}
          </div>

          {/* CTA */}
          <div className="mt-6 flex flex-wrap gap-3">
            <button
              onClick={() =>
                window.scrollTo({ top: 0, behavior: "smooth" })
              }
              className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white"
            >
              Back to top
            </button>
            <a
              className="inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm"
              href={`https://www.google.com/search?q=${encodeURIComponent(
                (data?.title || "") + " recipe"
              )}`}
              target="_blank"
              rel="noreferrer"
            >
              Find similar
              <svg
                viewBox="0 0 24 24"
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M18 13v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                <path d="M15 3h6v6" />
                <path d="M10 14L21 3" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
